import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import CustomerLayout from '../../components/CustomerLayout';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { realtimeChatAPI } from '../../lib/realtimeChatApi';
import useChatSocket from '../../hooks/useChatSocket';
import { uploadChatMedia } from '../../lib/chatUpload';
import { ChatHeader, ChatInput, ChatMessageList } from '../../components/chat';
import { ensureNotificationPermission, notifyIncoming, playBeep } from '../../lib/notify';

const CustomerChatPage = () => {
  const [threadId, setThreadId] = useState(null);
  const [messagesByThread, setMessagesByThread] = useState({});
  const [draft, setDraft] = useState('');
  const [uploadPct, setUploadPct] = useState(null);
  const [sending, setSending] = useState(false);

  // Typing indicator state
  const [remoteTyping, setRemoteTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  // Block state
  const [threadBlocked, setThreadBlocked] = useState(false);
  const [blockedByName, setBlockedByName] = useState(null);

  const chatListRef = useRef(null);
  const inputRef = useRef(null);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const [userAtBottom, setUserAtBottom] = useState(true);


  const scrollToBottom = useCallback(() => {
    const run = () => {
      const el = chatListRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
      setShowJumpToBottom(false);
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }, []);

  const scheduleScrollToBottom = useCallback((delay = 80) => {
    setTimeout(scrollToBottom, delay);
  }, [scrollToBottom]);

  const isNearBottom = useCallback(() => {
    const el = chatListRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  const token = localStorage.getItem('customer_token');
  const { user, refreshUser } = useCustomerAuth();

  useEffect(() => {
    ensureNotificationPermission();
  }, []);

  useEffect(() => {
    refreshUser?.();
  }, [refreshUser]);

  const activeMessages = useMemo(() => {
    if (!threadId) return [];
    return messagesByThread[String(threadId)] || [];
  }, [messagesByThread, threadId]);

  const mergeIncoming = useCallback((tid, msg) => {
    if (!tid || !msg) return;
    setMessagesByThread((prev) => {
      const key = String(tid);
      const existing = prev[key] ? [...prev[key]] : [];

      const idxById = msg.id ? existing.findIndex((m) => Number(m.id) === Number(msg.id)) : -1;
      if (idxById >= 0) {
        const next = [...existing];
        next[idxById] = { ...next[idxById], ...msg };
        return { ...prev, [key]: next };
      }

      if (msg.client_id) {
        const idxByClient = existing.findIndex((m) => m.client_id && m.client_id === msg.client_id);
        if (idxByClient >= 0) {
          const next = [...existing];
          next[idxByClient] = { ...next[idxByClient], ...msg };
          return { ...prev, [key]: next };
        }
      }

      return { ...prev, [key]: [...existing, msg] };
    });
  }, []);

  const handleWsEvent = useCallback(
    (evt) => {
      if (!evt || typeof evt !== 'object') return;
      if (evt.type === 'message_created') {
        const tid = evt.thread_id || threadId;
        const msg = evt.message;

        const shouldJump = tid === threadId && isNearBottom();
        mergeIncoming(tid, msg);

        if (tid === threadId) {
          if (userAtBottom) {
            scheduleScrollToBottom();
            // If user is viewing the bottom and tab is active, mark as read immediately
            if (!document.hidden && msg?.id) {
              realtimeChatAPI.markRead(tid, msg.id).catch(() => {});
            }
          } else {
            setShowJumpToBottom(true);
          }
        }

        // Notify only for incoming messages when tab inactive
        if (document.hidden && msg && msg.sender_role === 'staff') {
          playBeep();
          notifyIncoming({
            title: 'New message',
            body: msg.text || 'Attachment',
          });
        }
      }
      if (evt.type === 'message_deleted_for_me') {
        if (!evt.message_id) return;
        setMessagesByThread((prev) => {
          const key = String(threadId);
          const list = prev[key] ? prev[key].filter((m) => Number(m.id) !== Number(evt.message_id)) : [];
          return { ...prev, [key]: list };
        });
      }
      if (evt.type === 'message_deleted_for_all') {
        if (!evt.message_id) return;
        setMessagesByThread((prev) => {
          const key = String(threadId);
          const list = prev[key]
            ? prev[key].map((m) => {
              if (Number(m.id) !== Number(evt.message_id)) return m;
              return { ...m, deleted_for_all: true, deleted_mode: evt.mode, masked: true, text: null, media: [], media_ids: [] };
            })
            : [];
          return { ...prev, [key]: list };
        });
      }
      if (evt.type === 'message_delivered') {
        const tid = evt.thread_id != null ? String(evt.thread_id) : String(threadId);
        const ids = new Set((evt.message_ids || []).map((id) => Number(id)).filter(Boolean));
        if (ids.size === 0) return;
        setMessagesByThread((prev) => {
          const list = prev[tid];
          if (!list) return prev;
          const next = list.map((m) => {
            if (m.sender_role !== 'customer') return m;
            if (!ids.has(Number(m.id))) return m;
            return { ...m, status: 'delivered', delivered_at: evt.delivered_at };
          });
          return { ...prev, [tid]: next };
        });
      }
      if (evt.type === 'message_read') {
        const tid = evt.thread_id != null ? String(evt.thread_id) : String(threadId);
        const lastId = evt.last_read_message_id != null ? Number(evt.last_read_message_id) : null;
        if (lastId == null) return;
        setMessagesByThread((prev) => {
          const list = prev[tid];
          if (!list) return prev;
          const next = list.map((m) => {
            if (m.sender_role !== 'customer') return m;
            if (Number(m.id) > lastId) return m;
            return { ...m, status: 'read', read_at: evt.read_at };
          });
          return { ...prev, [tid]: next };
        });
      }
      if (evt.type === 'typing' && evt.sender_role === 'staff') {
        setRemoteTyping(true);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setRemoteTyping(false), 3000);
      }
      if (evt.type === 'thread_blocked') {
        setThreadBlocked(!!evt.blocked);
        setBlockedByName(evt.blocked ? (evt.blocked_by_name || 'Staff') : null);
      }
      if (evt.type === 'thread_deleted') {
        setThreadId(null);
        setMessagesByThread({});
        toast.info('This chat has been deleted');
      }
    },
    [threadId, mergeIncoming, scheduleScrollToBottom]
  );

  const { connected: wsConnected, sendJson: wsSendJson } = useChatSocket({
    threadId,
    token,
    enabled: !!threadId && !!token,
    onEvent: handleWsEvent,
  });

  useEffect(() => {
    const run = async () => {
      try {
        const res = await realtimeChatAPI.customerThread();
        const tid = res.data.thread?.id;
        if (tid) setThreadId(tid);
      } catch (e) {
        toast.error('Failed to open chat');
      }
    };
    run();
  }, []);

  // Mark-as-read when the customer opens the chat / returns to tab
  useEffect(() => {
    const markVisible = async () => {
      if (!threadId) return;
      if (document.hidden) return;
      const lastId = (activeMessages || []).slice(-1)[0]?.id;
      if (!lastId) return;
      try {
        await realtimeChatAPI.markRead(threadId, lastId);
      } catch (e) {
        // ignore
      }
    };

    markVisible();
    document.addEventListener('visibilitychange', markVisible);
    return () => document.removeEventListener('visibilitychange', markVisible);
  }, [threadId, activeMessages]);

  useEffect(() => {
    const run = async () => {
      if (!threadId) return;
      try {
        const res = await realtimeChatAPI.listMessages(threadId, { limit: 30 });
        setMessagesByThread((prev) => ({ ...prev, [String(threadId)]: res.data.messages || [] }));
        // Load block status from thread
        const thread = res.data.thread;
        if (thread) {
          setThreadBlocked(!!thread.blocked);
          setBlockedByName(thread.blocked ? (thread.blocked_by_name || 'Staff') : null);
        }
        const lastId = (res.data.messages || []).slice(-1)[0]?.id;
        if (lastId) await realtimeChatAPI.markRead(threadId, lastId);
        scheduleScrollToBottom(120);
      } catch (e) {
        // ignore
      }
    };
    run();
  }, [threadId, scheduleScrollToBottom]);

  const sendText = async () => {
    if (!threadId) return;
    const text = (draft || '').trim();
    if (!text) return;

    setSending(true);
    try {
      const clientId = `c-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const optimistic = {
        id: `tmp-${Date.now()}`,
        client_id: clientId,
        sender_role: 'customer',
        sender_name: 'You',
        text,
        created_at: new Date().toISOString(),
      };
      mergeIncoming(threadId, optimistic);
      setDraft('');

      const res = await realtimeChatAPI.sendMessage(threadId, { text, client_id: clientId });
      const msg = res.data.message;
      try {
        window.dispatchEvent(new Event('chat:activity'));
      } catch (e) {
        // ignore
      }
      setMessagesByThread((prev) => {
        const key = String(threadId);
        const list = prev[key] ? prev[key].filter((m) => String(m.client_id || '') !== String(clientId)) : [];
        return { ...prev, [key]: [...list, msg] };
      });
      scheduleScrollToBottom();

      wsSendJson?.({ type: 'mark_read', last_read_message_id: msg.id });
    } catch (e) {
      toast.error('Failed to send');
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus?.(), 0);
    }
  };

  const sendAttachment = async (file) => {
    if (!threadId || !file) return;

    const kind = (file.type || '').startsWith('image/') ? 'image' : 'file';
    setUploadPct(0);
    try {
      const media = await uploadChatMedia({
        file,
        kind,
        onProgress: ({ percent }) => setUploadPct(percent),
        api: realtimeChatAPI,
      });
      const clientId = `c-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const res = await realtimeChatAPI.sendMessage(threadId, { media_ids: [media.id], client_id: clientId });
      if (res.data?.message) {
        mergeIncoming(threadId, { ...res.data.message, client_id: clientId });
        scheduleScrollToBottom();
      }
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploadPct(null);
    }
  };

  const sendVoice = async (blob) => {
    if (!threadId || !blob) return;
    const file = new File([blob], `voice-${Date.now()}.webm`, { type: blob.type || 'audio/webm' });

    setUploadPct(0);
    try {
      const media = await uploadChatMedia({
        file,
        kind: 'audio',
        onProgress: ({ percent }) => setUploadPct(percent),
        api: realtimeChatAPI,
      });
      const clientId = `c-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const res = await realtimeChatAPI.sendMessage(threadId, { media_ids: [media.id], client_id: clientId });
      if (res.data?.message) {
        mergeIncoming(threadId, { ...res.data.message, client_id: clientId });
        scheduleScrollToBottom();
      }
    } catch (e) {
      toast.error('Voice upload failed');
    } finally {
      setUploadPct(null);
    }
  };

  return (
    <CustomerLayout>
      <div className="h-full">
        <div className="panel border border-border rounded-lg shadow-sm h-full p-0 overflow-hidden flex flex-col min-h-0 bg-card">
          <ChatHeader
            title="Support"
            subtitle={
              remoteTyping ? (
                <span data-testid="typing-indicator" className="text-green-600 font-medium">typing...</span>
              ) : (
                wsConnected ? 'Connected' : 'Reconnecting…'
              )
            }
            showAdminActions={false}
          />

          {threadBlocked && (
            <div data-testid="blocked-notice" className="px-4 py-2 bg-red-50 border-b border-red-200 text-xs text-red-600 font-medium">
              Chat has been blocked
            </div>
          )}

          <ChatMessageList
            listRef={chatListRef}
            messages={activeMessages}
            roleThatIsMe="customer"
            mediaUrl={(id) => realtimeChatAPI.mediaUrl(id)}
            showSenderName={false}
            appRatingToken={token || undefined}
            existingAppRating={user?.app_rating}
            onAppRated={refreshUser}
            onScroll={() => {
              const near = isNearBottom();
              setUserAtBottom(near);
              if (near) setShowJumpToBottom(false);
            }}
            showJumpToBottom={showJumpToBottom}
            onJumpToBottom={scrollToBottom}
            emptyMessage="No messages yet. Start the conversation."
          />

          <ChatInput
            value={draft}
            onChange={setDraft}
            onSend={sendText}
            onAttach={sendAttachment}
            onVoiceRecorded={sendVoice}
            placeholder="Type a message"
            disabled={!threadId || threadBlocked}
            sending={sending}
            uploadPct={uploadPct}
            onTyping={() => {
              const now = Date.now();
              if (now - lastTypingSentRef.current > 2000) {
                lastTypingSentRef.current = now;
                wsSendJson?.({ type: 'typing' });
              }
            }}
            inputRef={inputRef}
          />
        </div>
      </div>
    </CustomerLayout>
  );
};

export default CustomerChatPage;
